import React, { useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarCheck, FastForward, Pin, UserPlus, Users, UserX } from 'lucide-react';
import { computeRealEndDate, employeeWorksOnDay, employeeNominalWorkday, employeeUnderContractOn, isFrenchPublicHoliday, isStandardWorkday } from '../utils/workDays';

export const fmt = (d) => (d ? format(d instanceof Date ? d : parseISO(d), 'dd/MM/yyyy', { locale: fr }) : '—');

// Bornes du mois souhaité ("YYYY-MM") : premier et dernier jour
const wishWindow = (startMonth) => {
  if (!startMonth) return null;
  const [y, m] = startMonth.split('-').map(Number);
  if (!y || !m) return null;
  return { first: new Date(y, m - 1, 1), last: new Date(y, m, 0) };
};

export const wishLabel = (startMonth) => {
  const w = wishWindow(startMonth);
  return w ? format(w.first, 'MMMM yyyy', { locale: fr }) : '—';
};

// Diagnostic du planning, partagé par le tableau de bord (bande d'indicateurs)
// et par la liste des chantiers (icône d'alerte sur chaque ligne) : un seul
// calcul, une seule vérité.
const usePlanningDiagnostics = (projects, employees) => useMemo(() => {
  const getEmployeeById = (id) => employees.find((e) => e.id === id);

  const projectEnd = (project) => {
    if (!project.actualStartDate) return null;
    const start = parseISO(project.actualStartDate);
    const team = (project.assignedEmployees || []).map(getEmployeeById).filter(Boolean);
    if (project.durationInDays) return computeRealEndDate(start, project.durationInDays, team);
    const end = new Date(start);
    end.setDate(start.getDate() + (project.duration || 1) * 7 - 1);
    return end;
  };

  const active = projects.filter((p) => p.status !== 'completed');

  const isAnchored = (p) => p.hasFixedStartDate || p.manuallyPlaced || p.status === 'in-progress';

  const overlaps = (a, b) => {
    const as = a.actualStartDate ? parseISO(a.actualStartDate) : null;
    const bs = b.actualStartDate ? parseISO(b.actualStartDate) : null;
    if (!as || !bs) return false;
    const ae = projectEnd(a);
    const be = projectEnd(b);
    return !(ae < bs || as > be);
  };

  // Part des jours ouvrés de la période où le salarié est réellement présent
  const availabilityRatio = (employee, start, end) => {
    let total = 0;
    let worked = 0;
    const d = new Date(start);
    while (d <= end) {
      if (d.getDay() >= 1 && d.getDay() <= 5 && !isFrenchPublicHoliday(d)) {
        total += 1;
        if (employeeWorksOnDay(employee, d)) worked += 1;
      }
      d.setDate(d.getDate() + 1);
    }
    return total === 0 ? 1 : worked / total;
  };

  // Le noyau seul (assignedEmployees) tombe parfois sous l'effectif minimum
  // au démarrage d'un chantier ancré, le temps qu'un renfort programmé
  // rejoigne — ce n'est pas un vrai manque si les renforts couvrent bien la
  // période. Compte les jours ouvrés RÉELLEMENT en dessous du minimum (noyau
  // + renforts confondus), et le plus grand nombre de personnes qui manquent.
  const understaffedDays = (p) => {
    const start = p.actualStartDate ? parseISO(p.actualStartDate) : null;
    const end = projectEnd(p);
    if (!start || !end) return { count: 0, from: null, to: null, missing: 0 };
    const min = p.minEmployees || 1;
    const core = (p.assignedEmployees || []).map(getEmployeeById).filter(Boolean);
    let count = 0, from = null, to = null, missing = 0;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const coreOn = core.filter((emp) => employeeWorksOnDay(emp, d)).length;
      const renforts = (p.extraAssignments || []).filter((x) => {
        const f = new Date(x.from); f.setHours(0, 0, 0, 0);
        const t = new Date(x.to); t.setHours(23, 59, 59, 999);
        return d >= f && d <= t;
      }).length;
      // Jour où le chantier ne tourne pas (repos de toute l'équipe) — sauf sans
      // équipe du tout : il tourne alors les jours ouvrés, entièrement à pourvoir.
      const activeDay = core.length === 0 ? isStandardWorkday(d) : coreOn > 0 || renforts > 0;
      if (!activeDay || coreOn + renforts >= min) continue;
      count += 1;
      missing = Math.max(missing, min - coreOn - renforts);
      if (!from) from = new Date(d);
      to = new Date(d);
    }
    return { count, from, to, missing };
  };

  // Cause RÉELLE du drapeau hasConflicts, pour un message utile
  const conflictReason = (p) => {
    const team = (p.assignedEmployees || []).map(getEmployeeById).filter(Boolean);
    const min = p.minEmployees || 1;
    const reasons = [];
    if (team.length < min) {
      const { count, from, to, missing } = understaffedDays(p);
      if (count > 0) {
        reasons.push(from.getTime() === to.getTime()
          ? `${missing} pers. à trouver le ${fmt(from)} (effectif minimum ${min}, renforts compris)`
          : `${missing} pers. à trouver du ${fmt(from)} au ${fmt(to)}, ${count} j. (effectif minimum ${min}, renforts compris)`);
      }
      // Sinon : le noyau seul est sous le minimum, mais les renforts déjà
      // programmés couvrent bien toute la période — rien à signaler ici.
    }
    if (p.isComplex && !team.some((e) => e.isQualified)) {
      reasons.push('aucun ouvrier qualifié sur un chantier complexe');
    }
    if (team.length > 0 && team.every((e) => e.isApprentice)) {
      reasons.push('uniquement des apprentis, sans encadrant');
    }
    const required = p.requiredSkills || [];
    if (required.length) {
      const have = new Set(team.flatMap((e) => e.skills || []));
      const missing = required.filter((s) => !have.has(s));
      if (missing.length) reasons.push(`spécialité(s) non couverte(s) : ${missing.join(', ')}`);
    }
    if (isAnchored(p)) {
      const clash = active.find(
        (q) => q.id !== p.id && isAnchored(q) && overlaps(p, q)
          && (q.assignedEmployees || []).some((id) => (p.assignedEmployees || []).includes(id))
      );
      if (clash) {
        const shared = (p.assignedEmployees || [])
          .filter((id) => (clash.assignedEmployees || []).includes(id))
          .map((id) => getEmployeeById(id)?.firstName)
          .filter(Boolean);
        reasons.push(`${shared.join(', ')} également sur « ${clash.name} » à la même période (deux chantiers bloqués)`);
      }
    }
    // Vide (pas de repli générique) : le point d'appel doit alors NE PAS
    // afficher d'alerte — un noyau sous le minimum entièrement couvert par
    // des renforts programmés n'a rien d'anormal à signaler.
    return reasons.length ? reasons.join(' · ') : null;
  };

  // --- Retards sur le mois de démarrage souhaité ---
  const late = active
    .map((p) => {
      const w = wishWindow(p.startMonth);
      if (!w || !p.actualStartDate) return null;
      const start = parseISO(p.actualStartDate);
      if (start <= w.last) return null;
      const days = Math.round((start - w.last) / 86400000);
      return { project: p, start, days };
    })
    .filter(Boolean)
    .sort((a, b) => b.days - a.days);

  // --- Alertes ---
  const alerts = [];
  active.forEach((p) => {
    const start = p.actualStartDate ? parseISO(p.actualStartDate) : null;
    if (p.hasConflicts) {
      const reason = conflictReason(p);
      if (reason) {
        const understaffed = (p.assignedEmployees || []).length < (p.minEmployees || 1);
        alerts.push({
          project: p, start, level: 'danger',
          icon: understaffed ? <UserPlus className="w-3.5 h-3.5" /> : <Users className="w-3.5 h-3.5" />,
          label: understaffed ? 'Besoin de personnel ponctuel' : "Conflit d'assignation",
          detail: reason,
        });
      }
      // Sinon : hasConflicts vient uniquement d'un noyau sous le minimum déjà
      // comblé par des renforts programmés — rien de réel à signaler.
    }
    if (p.deadlineExceeded) {
      alerts.push({
        project: p, start, level: 'danger', icon: <CalendarCheck className="w-3.5 h-3.5" />,
        label: 'Date de fin exigée dépassée',
        detail: `Fin prévue le ${fmt(projectEnd(p))}, exigée avant le ${fmt(p.deadline)}`,
      });
    }
    if (p.advancedFromMonth) {
      alerts.push({
        project: p, start, level: 'info', icon: <FastForward className="w-3.5 h-3.5" />,
        label: 'Avancé avant le mois souhaité',
        detail: `Démarre le ${fmt(start)} au lieu de ${wishLabel(p.advancedFromMonth)} — pour occuper l'équipe`,
      });
    }
    const end = projectEnd(p);

    // Sous-effectif RÉSIDUEL — mais seulement quand c'est ANORMAL : un jour
    // où l'équipe NOMINALE (planning habituel de chacun, hors congé/maladie/
    // formation) aurait suffi, mais où une absence EXCEPTIONNELLE la fait
    // chuter sous le minimum. Un jour de repos hebdomadaire habituel (ex. un
    // apprenti à 4j/semaine, absent tous les vendredis) N'EST PAS une alerte :
    // c'est le fonctionnement normal du chantier, déjà intégré dans le calcul
    // de sa durée — le signaler à chaque vendredi de chaque chantier était un
    // faux positif systématique (bug constaté : Delon Thierry, Merlier flavin,
    // Les primevères, Emilie Leloy, GILLES AYRAL... tous à cause d'un simple
    // jour de repos, pas d'une vraie absence).
    if (start && end && p.status !== 'completed') {
      const team = (p.assignedEmployees || []).map(getEmployeeById).filter(Boolean);
      const minReq = p.minEmployees || 1;
      if (team.length > 0) {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const rangeStart = start > today ? start : today;
        let shortDay = null, shortMissing = 0;
        for (let d = new Date(rangeStart); d <= end && !shortDay; d.setDate(d.getDate() + 1)) {
          // Équipe qui devrait normalement être là ce jour (hors absences) :
          // si elle est déjà sous le minimum, rien d'anormal à signaler.
          const nominalCount = team.filter((emp) =>
            employeeNominalWorkday(emp, d) && employeeUnderContractOn(emp, d)
          ).length;
          if (nominalCount < minReq) continue;
          const onsite = team.filter((emp) => employeeWorksOnDay(emp, d)).length
            + (p.extraAssignments || []).filter((x) => {
                const from = new Date(x.from); from.setHours(0, 0, 0, 0);
                const to = new Date(x.to); to.setHours(23, 59, 59, 999);
                return d >= from && d <= to;
              }).length;
          if (onsite < minReq) {
            shortDay = new Date(d);
            shortMissing = minReq - onsite;
          }
        }
        if (shortDay) {
          alerts.push({
            project: p, start, level: 'warn', icon: <UserPlus className="w-3.5 h-3.5" />,
            label: 'Besoin de personnel ponctuel',
            detail: `Le ${fmt(shortDay)}, une absence laisse l'équipe sous le minimum (${minReq}) : ${shortMissing} pers. à trouver`,
          });
        }
      }
    }

    if (start && end) {
      (p.assignedEmployees || []).forEach((id) => {
        const emp = getEmployeeById(id);
        if (!emp) return;
        const ratio = availabilityRatio(emp, start, end);
        if (ratio < 0.2) {
          alerts.push({
            project: p, start, level: 'warn', icon: <UserX className="w-3.5 h-3.5" />,
            label: 'Salarié absent sur la période',
            detail: `${emp.fullName} est présent ${Math.round(ratio * 100)} % du chantier (congés, arrêt, formation ou fin de contrat) — à remplacer`,
          });
        }
      });
    }
    if (p.manuallyPlaced && !p.hasFixedStartDate) {
      alerts.push({
        project: p, start, level: 'warn', icon: <Pin className="w-3.5 h-3.5" />,
        label: 'Posé à la main',
        detail: 'Ce chantier n\'est plus replanifié automatiquement (menu ⋯ > Libérer)',
      });
    }
  });
  const levelRank = { danger: 0, warn: 1, info: 2 };
  alerts.sort((a, b) => levelRank[a.level] - levelRank[b.level] || (a.start || 0) - (b.start || 0));

  const alertsByProject = {};
  alerts.forEach((a) => {
    if (!alertsByProject[a.project.id]) alertsByProject[a.project.id] = [];
    alertsByProject[a.project.id].push(a);
  });

  return { projectEnd, late, alerts, alertsByProject };
}, [projects, employees]);

export default usePlanningDiagnostics;
